import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Search } from "lucide-react";
import { api, assetUrl } from "../../adminApi";
import { Card, SectionTitle, Btn } from "../../components/admin/AdminUI";
import ProjectForm, { CATEGORIES } from "./ProjectForm";

export default function AdminProjects() {
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} | project
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [msg, setMsg] = useState("");

  const load = () => api.projects().then(setList).catch(() => setList([]));
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!list) return [];
    return list.filter(
      (p) =>
        (cat === "All" || p.category === cat) &&
        (!q || (p.title?.en || "").toLowerCase().includes(q.toLowerCase()) ||
          (p.location || "").toLowerCase().includes(q.toLowerCase()))
    );
  }, [list, q, cat]);

  const remove = async (p) => {
    if (!window.confirm(`"${p.title?.en}" projesini silmek istediğinize emin misiniz?`)) return;
    await api.deleteProject(p.id);
    setMsg("Proje silindi.");
    load();
  };

  return (
    <div data-testid="admin-projects">
      <SectionTitle
        label="[ PORTFOLYO ]"
        title="Projeler"
        right={
          !editing && (
            <Btn onClick={() => setEditing({})} data-testid="project-new">
              <Plus size={13} strokeWidth={1.8} /> YENİ PROJE
            </Btn>
          )
        }
      />

      {msg && <p className="mono text-[9px] text-[#8C7862] mb-5">{msg}</p>}

      {editing && (
        <ProjectForm
          project={editing.id ? editing : null}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setMsg("Değişiklikler kaydedildi ve sitede yayında.");
            load();
          }}
        />
      )}

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative w-full sm:w-[280px]">
          <Search size={13} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A8A29A]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Proje ara..."
            data-testid="project-search"
            className="w-full bg-white border border-[#E2E0D8] pl-9 pr-3.5 py-3 text-[14px] outline-none focus:border-[#C8B29B] transition-colors"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {["All", ...CATEGORIES].map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`filter-${c}`}
              className={`mono text-[9px] px-3 py-2 border transition-colors ${
                cat === c
                  ? "bg-[#141414] text-[#FAFAFA] border-[#141414]"
                  : "bg-white text-[#141414] border-[#E2E0D8] hover:border-[#C8B29B]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {!list ? (
        <Loader2 size={18} className="animate-spin text-[#8C7862]" />
      ) : (
        <Card>
          <div className="hidden lg:grid grid-cols-[88px_1fr_140px_120px_100px_120px] gap-4 px-5 py-3.5 border-b border-[#E2E0D8] bg-[#FAFAFA]">
            {["GÖRSEL", "PROJE", "KATEGORİ", "KONUM", "YIL", ""].map((h, i) => (
              <span key={i} className="mono text-[8.5px] text-[#A8A29A]">
                {h}
              </span>
            ))}
          </div>
          {filtered.map((p) => (
            <div
              key={p.id}
              data-testid={`project-row-${p.slug}`}
              className="grid grid-cols-[64px_1fr_auto] lg:grid-cols-[88px_1fr_140px_120px_100px_120px] gap-4 items-center px-5 py-4 border-b border-[#E2E0D8] last:border-b-0 hover:bg-[#FAFAFA] transition-colors"
            >
              <div className="w-full h-[52px] bg-[#F2F0EB] border border-[#E2E0D8] overflow-hidden">
                {p.images?.[0] ? (
                  <img src={assetUrl(p.images[0])} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="mono text-[7.5px] text-[#A8A29A] flex items-center justify-center h-full">YOK</span>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-[15px] text-[#141414] truncate">{p.title?.en || p.slug}</p>
                <p className="mono text-[8.5px] text-[#A8A29A] mt-1 lg:hidden">
                  {p.category} · {p.location}
                </p>
                <p className="mono text-[8.5px] text-[#A8A29A] mt-1 hidden lg:block">{p.slug}</p>
              </div>
              <span className="mono text-[9px] text-[#6B675F] hidden lg:block">{p.category}</span>
              <span className="text-[13px] text-[#141414]/70 hidden lg:block truncate">{p.location}</span>
              <span className="mono text-[9px] text-[#6B675F] hidden lg:block">{p.year}</span>
              <div className="flex gap-1.5 justify-end">
                <button
                  onClick={() => setEditing(p)}
                  data-testid={`edit-${p.slug}`}
                  className="w-9 h-9 border border-[#E2E0D8] bg-white flex items-center justify-center hover:border-[#C8B29B] transition-colors"
                >
                  <Pencil size={13} strokeWidth={1.7} />
                </button>
                <button
                  onClick={() => remove(p)}
                  data-testid={`delete-${p.slug}`}
                  className="w-9 h-9 border border-[#E2E0D8] bg-white flex items-center justify-center text-[#8C2E1D] hover:border-[#D9B7AE] transition-colors"
                >
                  <Trash2 size={13} strokeWidth={1.7} />
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="mono text-[9.5px] text-[#6B675F] py-14 text-center">SONUÇ BULUNAMADI</p>
          )}
        </Card>
      )}
    </div>
  );
}
